import { Link } from "react-router-dom";
import type { CtaTreeInfo } from "../useEditorMap";

const RISK_LEGEND: Array<{ code: string; label: string; color: string }> = [
  { code: "BAIXO", label: "Baixo", color: "#16a34a" },
  { code: "MEDIO", label: "Médio", color: "#ca8a04" },
  { code: "ALTO", label: "Alto", color: "#ea580c" },
  { code: "CRITICO", label: "Crítico", color: "#dc2626" },
  { code: "", label: "Sem vistoria validada", color: "#64748b" },
];

const STATUS_LABELS: Record<string, string> = {
  ATIVO: "Ativo",
  CAIDA: "Caído",
  MORTA: "Morto",
  SUPRIMIDA: "Suprimido",
  DESAPARECIDA: "Desaparecido",
};

interface CtaTreesLayerControlProps {
  count: number;
  loading: boolean;
  error: string | null;
  visible: boolean;
  onToggleVisible: (visible: boolean) => void;
  onZoom: () => void;
  scopeLabel: string;
}

export function CtaTreesLayerControl({ count, loading, error, visible, onToggleVisible, onZoom, scopeLabel }: CtaTreesLayerControlProps) {
  return (
    <div className="p-3 border-b border-background-100">
      <p className="text-[11px] font-semibold text-foreground-500 uppercase tracking-wide mb-2">Exemplares cadastrados (CTA)</p>
      <label className="flex items-center gap-2 text-xs text-foreground-700 cursor-pointer">
        <input type="checkbox" checked={visible} onChange={(e) => onToggleVisible(e.target.checked)} />
        <span className="flex-1">Mostrar no mapa</span>
        <span className="text-foreground-500">{loading ? "…" : count}</span>
      </label>
      <p className="text-[10px] text-foreground-400 mt-1">{scopeLabel} · somente leitura</p>
      {error && <p className="text-[11px] text-red-600 mt-1">{error}</p>}
      {!loading && !error && count === 0 && (
        <p className="text-[11px] text-foreground-400 mt-1">Nenhum exemplar com coordenada neste projeto.</p>
      )}
      {count > 0 && (
        <button onClick={onZoom} className="mt-2 text-[11px] text-primary-600 hover:underline cursor-pointer">
          Enquadrar exemplares
        </button>
      )}
      <div className="mt-2 grid grid-cols-1 gap-0.5">
        {RISK_LEGEND.map((r) => (
          <span key={r.code} className="flex items-center gap-1.5 text-[10px] text-foreground-500">
            <span className="w-2.5 h-2.5 rounded-full border border-white shadow-sm" style={{ background: r.color }} />
            {r.code ? `Risco ${r.label.toLowerCase()}` : r.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function formatDate(value: string | null): string {
  if (!value) return "—";
  // Data sem hora: new Date() a leria como meia-noite UTC e mostraria o dia anterior.
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (day) return `${day[3]}/${day[2]}/${day[1]}`;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString("pt-BR");
}

export function CtaTreeCard({ tree, onClose }: { tree: CtaTreeInfo; onClose: () => void }) {
  const risk = RISK_LEGEND.find((r) => r.code === (tree.riskClass ?? "")) ?? RISK_LEGEND[RISK_LEGEND.length - 1];
  const address = [tree.street, tree.neighborhood].filter(Boolean).join(", ");
  return (
    <div className="p-4 space-y-3 text-xs">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-[10px] uppercase tracking-wide text-foreground-400">Exemplar do CTA</p>
          <p className="text-sm font-semibold text-foreground-900">{tree.ctaCode ?? `#${tree.id}`}</p>
        </div>
        <button onClick={onClose} aria-label="Fechar" className="text-foreground-400 hover:text-foreground-700 cursor-pointer">
          <i className="ri-close-line text-base" />
        </button>
      </div>
      <div>
        <p className="text-foreground-800">{tree.speciesPopularName ?? "Espécie não identificada"}</p>
        {tree.speciesScientificName && <p className="italic text-foreground-500">{tree.speciesScientificName}</p>}
      </div>
      <dl className="grid grid-cols-2 gap-x-2 gap-y-1.5">
        <dt className="text-foreground-400">Situação</dt>
        <dd className="text-foreground-700">{tree.status ? STATUS_LABELS[tree.status] ?? tree.status : "—"}</dd>
        <dt className="text-foreground-400">Risco</dt>
        <dd className="flex items-center gap-1.5 text-foreground-700">
          <span className="w-2 h-2 rounded-full" style={{ background: risk.color }} />
          {tree.riskClass ? risk.label : "Sem vistoria validada"}
        </dd>
        <dt className="text-foreground-400">Última vistoria</dt>
        <dd className="text-foreground-700">{formatDate(tree.latestInspectionDate)}</dd>
        <dt className="text-foreground-400">Coordenada</dt>
        <dd className="text-foreground-700">
          {tree.coordinate[1].toFixed(6)}, {tree.coordinate[0].toFixed(6)}
        </dd>
      </dl>
      {address && <p className="text-foreground-600">{address}</p>}
      <div className="flex flex-col gap-1.5 pt-1">
        <Link to={`/cta/${tree.id}`} className="text-center rounded-md bg-primary-500 text-white py-1.5 hover:bg-primary-600">
          Abrir ficha (FCA)
        </Link>
        <Link to={`/cta/${tree.id}/inspecoes/nova`} className="text-center rounded-md border border-background-200 text-foreground-700 py-1.5 hover:bg-background-50">
          Nova vistoria
        </Link>
      </div>
      <p className="text-[10px] text-foreground-400">
        Camada somente leitura. Para corrigir a posição, use "Editar cadastro" na ficha do exemplar.
      </p>
    </div>
  );
}
